import { Injectable } from '@angular/core';

/** Owns the application metadata schema and bounded transactions, never credential storage. */
@Injectable({ providedIn: 'root' })
export class AppMetadataStore {
  private readonly databaseName = 'RecoveryTracAppMetadata';
  private readonly databaseVersion = 2;
  private readonly timeoutMs = 30000;

  readOrCreateInstallationId(create: () => string): Promise<string> {
    return this.withStore<string>('metadata', (store, complete) => {
      const read = store.get('InstallationId');
      read.onsuccess = () => {
        complete(() => {
          const saved = read.result;
          if (saved !== undefined && (typeof saved !== 'string' || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(saved))) {
            throw new Error('Invalid saved installation identifier');
          }
          const identifier = saved || create();
          if (!saved) { store.put(identifier, 'InstallationId'); }
          return identifier;
        });
      };
    });
  }

  /** Serializes a synchronous session-storage action against other cooperating app runtimes. */
  withSessionLock<Result>(action: () => Result): Promise<Result> {
    return this.withStore<Result>('coordination', (store, complete) => {
      store.get('session').onsuccess = () => {
        complete(action);
      };
    });
  }

  private withStore<Result>(name: 'metadata' | 'coordination', action: (store: IDBObjectStore, complete: (calculate: () => Result) => void) => void): Promise<Result> {
    return new Promise((resolve, reject) => {
      let database: IDBDatabase | undefined;
      let transaction: IDBTransaction | undefined;
      let result: Result;
      let settled = false;
      const finish = (error?: unknown) => {
        if (settled) { return; }
        settled = true;
        clearTimeout(timer);
        database?.close();
        if (error) { reject(error); }
        else { resolve(result); }
      };
      const timer = setTimeout(() => {
        finish(new Error('Application metadata transaction timed out'));
        try { transaction?.abort(); } catch {}
      }, this.timeoutMs);
      try {
        const request = indexedDB.open(this.databaseName, this.databaseVersion);
        request.onupgradeneeded = () => {
          const opened = request.result;
          for (const store of ['metadata', 'coordination']) {
            if (!opened.objectStoreNames.contains(store)) { opened.createObjectStore(store); }
          }
        };
        request.onerror = () => finish(request.error || new Error('Application metadata unavailable'));
        request.onblocked = () => finish(new Error('Application metadata upgrade blocked'));
        request.onsuccess = () => {
          database = request.result;
          database.onversionchange = () => database.close();
          if (settled) { database.close(); return; }
          try {
            transaction = database.transaction(name, 'readwrite');
            transaction.oncomplete = () => finish();
            transaction.onabort = () => finish(transaction.error || new Error('Application metadata transaction aborted'));
            transaction.onerror = () => finish(transaction.error || new Error('Application metadata transaction failed'));
            action(transaction.objectStore(name), calculate => {
              if (settled) { return; }
              try { result = calculate(); }
              catch (error) {
                finish(error);
                try { transaction?.abort(); } catch {}
              }
            });
          } catch (error) {
            try { transaction?.abort(); } catch {}
            finish(error);
          }
        };
      } catch (error) {
        finish(error);
      }
    });
  }
}